import React from "react";

/**
 * Top-level error boundary. Prevents an uncaught render error in any single
 * component from blanking the entire app to a white screen.
 *
 * Renders a calm fallback UI with a "Reload" action. Logs the error to the
 * console so devs can still diagnose. Reset on route change is not automatic;
 * users can reload or navigate away via the browser.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        className="min-h-screen bg-[#FAF7EF] bg-grain flex items-center justify-center p-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">TARU</div>
          <div className="font-serif text-3xl mt-2">Something went off-script.</div>
          <div className="text-[#4A5A50] mt-3 text-sm">
            A component ran into an unexpected error. Your data is safe — a reload usually fixes it.
          </div>
          <button
            onClick={this.handleReload}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white px-5 py-2 text-sm"
            data-testid="error-boundary-reload"
          >
            Reload page
          </button>
          {process.env.NODE_ENV !== "production" && (
            <pre className="mt-6 text-left text-[11px] text-red-700 bg-white/70 border border-[#E4DFCF] rounded-lg p-3 overflow-auto max-h-64">
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
